<template>
  <div>
    <FilterBar>
      <slot name="filterbar" />
      <GenericMultiFilter
        :options="industryOptions"
        v-model="selectedIndustries"
        title="Industry"
        chip-title-field="label"
        @update:model-value="refreshData" />
      <OwnershipTypeFilter
        v-model="selectedOwnershipTypes"
        @update:model-value="refreshData" />
      <GenericRangeFilter
        v-model="appliedLimitRange"
        title="Program Limit"
        min-title="Min"
        max-title="Max"
        :suggested-range="[]"
        :value-formatter-fn="(val) => (val != null ? filters.compactNumber(val) : '')"
        data-test-prefix="limit"
        @update:model-value="refreshData" />
    </FilterBar>
    <div class="inner">
      <div class="content-grid">
        <div>
          <h2 class="mb-2">Limit By Industry</h2>
          <div
            v-if="loading"
            class="space-y-2">
            <Skeleton
              v-for="i in 8"
              :key="i"
              shape="rectangle"
              class="w-full"
              height="44px" />
          </div>
          <table
            v-if="!loading"
            v-tablesort
            class="apptable sortable dense mt-4">
            <thead>
              <tr>
                <td>Top Level Industry</td>
                <td class="number sorted">Count of Programs</td>
                <td class="money">Median Limit</td>
                <td class="money">Average Limit</td>
                <td class="money">Max Limit</td>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="result in benchmarkingResults"
                :key="result.genericIndustry">
                <td :title="result.genericIndustry">{{ filters.truncate(result.genericIndustry, 50) }}</td>
                <td class="number">{{ result.countOfPrograms ?? '--' }}</td>
                <td class="money">{{ numToMoneyString(result.medianLimit, 0) ?? '--' }}</td>
                <td class="money">{{ numToMoneyString(result.averageLimit, 0) ?? '--' }}</td>
                <td class="money">{{ numToMoneyString(result.maxLimit, 0) ?? '--' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <fusioncharts
            type="column2d"
            width="100%"
            height="500"
            dataFormat="json"
            :dataSource="chartData"
            data-test="chart" />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { umbrellaBenchmarkingService } from '@/services/api';
import { compactMoney, numToMoneyString } from '@/utilities/Money';
import { UmbrellaCarrierBenchmark } from '@/types/Benchmark';
import { LabelValue } from '@/types/label-value';
import FilterBar from '@/components/v2/FilterBar.vue';
import GenericMultiFilter from '@/components/v2/GenericMultiFilter.vue';
import GenericRangeFilter from '@/components/v2/GenericRangeFilter.vue';
import OwnershipTypeFilter from '@/components/OwnershipTypeFilter.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import type { LabelFilterType } from '@/types/components/FilterType';
import { filters } from '@/utilities/Filters';

const histogramChart = (labelStep: number): unknown => ({
  animation: false,
  caption: 'Total Limit Distribution',
  captionFontSize: 21,
  decimals: '0',
  xAxisName: 'Total Limit',
  yAxisName: 'Count of Programs',
  plotToolText: 'Limit: $label, Count: $value',
  showvalues: true,
  slantLabel: true,
  rotateLabels: true,
  labelStep,
  theme: 'cacs',
  exportFilename: 'CACS Umbrella/Excess Total Limits Histogram',
});

export default {
  name: 'UmbrellaBenchmarking',
  components: { FilterBar, GenericMultiFilter, GenericRangeFilter, OwnershipTypeFilter, Skeleton },
  data: () => ({
    filters,
    loading: true,
    filtersDirty: false,
    benchmarkingResults: [] as UmbrellaCarrierBenchmark[],
    histogramResults: [] as LabelValue[],
    industries: undefined as LabelFilterType<string>[] | undefined,
    appliedIndustries: [] as string[],
    selectedOwnershipTypes: [] as string[],
    appliedLimitRange: [] as number[],
    selectedIndustries: [] as string[],
  }),
  methods: {
    async refreshData(): Promise<void> {
      this.loading = true;
      const ownership = this.selectedOwnershipTypes;

      const [benchmarkResults, histogramResults] = await Promise.all([
        await umbrellaBenchmarkingService.LimitsByCarrier(this.selectedIndustries, ownership, this.appliedLimitRange),
        await umbrellaBenchmarkingService.TotalLimits(this.selectedIndustries, ownership, this.appliedLimitRange),
      ]);

      this.benchmarkingResults = (benchmarkResults || []).sort((a, b) => b.countOfPrograms - a.countOfPrograms);
      if (this.industries === undefined) {
        this.industries = this.benchmarkingResults
          .map((r) => ({ label: r.genericIndustry, value: r.genericIndustry }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }
      this.histogramResults = (histogramResults || []).map((x) => {
        return {
          label: `${compactMoney(x.limitValue)} - ${compactMoney(x.limitValue + 10_000_000)} `,
          value: x.countOfResults,
          showValue: x.countOfResults > 0,
        };
      });
      this.loading = false;
    },
    numToMoneyString,
    compactMoney,
  },
  computed: {
    breadcrumbs() {
      return [
        { text: 'Home', exact: true, to: '/accounts' },
        { text: 'P&C Umbrella Benchmarking', disabled: true },
      ];
    },
    chartData(): { chart: unknown; data: LabelValue[] } {
      return { chart: histogramChart(this.histogramResults.length / 8), data: this.histogramResults };
    },
    industryOptions(): { label: string; value: string }[] {
      return this.industries ?? [];
    },
  },
  created() {
    this.refreshData();
  },
};
</script>

<style scoped>
.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(800px, 1fr));
  column-gap: 2rem;
}
</style>
